// src/products/capRaise/pages/TermsheetPage.js
// Standalone Termsheet page for generating investment termsheets

import React, { useState, useEffect, useCallback } from 'react';
import { 
  FileText, 
  RefreshCw,
  AlertCircle,
  Download,
  FileText as WordIcon,
  ExternalLink
} from 'lucide-react';
import { supabase } from '../../../config';
import TermsheetGenerator from '../components/TermsheetGenerator';
// import CurrencyService from '../../../services/currencyService'; // Unused
import { useMagnitude } from '../../../components/ui/MagnitudeSelector';

const TermsheetPage = ({ selectedCompany, activeRound, user, addMessage }) => {
  const [roundData, setRoundData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [termsheetGenerated, setTermsheetGenerated] = useState(false);
  const [showExportMenu, setShowExportMenu] = useState(false);
  const { formatCurrencyWithMagnitude } = useMagnitude('millions');

  // Load round data for termsheet generation
  const loadRoundData = useCallback(async () => {
    if (!activeRound) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      
      // Load full round data including terms configuration
      const { data: roundDetails, error } = await supabase
        .from('capraise_rounds')
        .select(`
          *,
          equity_companies (
            id,
            name,
            description,
            address,
            currency
          )
        `)
        .eq('id', activeRound.id)
        .single();

      if (error) throw error;

      if (roundDetails) {
        setRoundData({
          round_name: roundDetails.round_name,
          round_type: roundDetails.round_type,
          target_amount: roundDetails.target_amount,
          pre_money_valuation: roundDetails.pre_money_valuation,
          minimum_amount: roundDetails.minimum_amount,
          maximum_amount: roundDetails.maximum_amount,
          lead_investor_amount: roundDetails.lead_investor_amount,
          currency: roundDetails.currency,
          description: roundDetails.description,
          terms_config: roundDetails.terms_config || {},
          use_of_funds: roundDetails.terms_config?.use_of_funds || [],
          company: roundDetails.equity_companies
        });
      }

    } catch (error) {
      console.error('Error loading round data:', error);
      addMessage?.('Error loading round data: ' + error.message, 'error');
    } finally {
      setLoading(false);
    }
  }, [activeRound, addMessage]);

  // Save termsheet configuration
  const handleSaveTermsheet = async (termsheetData) => {
    if (!activeRound) return;

    try {
      setSaving(true);
      
      const { error } = await supabase
        .from('capraise_rounds')
        .update({ 
          termsheet_config: termsheetData,
          termsheet_updated_at: new Date().toISOString()
        })
        .eq('id', activeRound.id);

      if (error) throw error;

      addMessage?.('Termsheet configuration saved successfully', 'success');
      setTermsheetGenerated(true);
    } catch (error) {
      console.error('Error saving termsheet:', error);
      addMessage?.('Error saving termsheet configuration', 'error');
    } finally {
      setSaving(false);
    }
  };

  // Export functions
  const exportToWord = () => {
    if (!roundData) return;
    
    // Create a simple HTML document that can be opened in Word
    const htmlContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="utf-8">
        <title>Termsheet - ${roundData.company?.name} - ${roundData.round_name}</title>
        <style>
          body { font-family: Arial, sans-serif; margin: 40px; line-height: 1.6; }
          h1 { color: #2563eb; border-bottom: 2px solid #2563eb; padding-bottom: 10px; }
          h2 { color: #1f2937; margin-top: 30px; }
          .term-row { margin: 10px 0; }
          .term-label { font-weight: bold; display: inline-block; width: 200px; }
          .highlight-value { font-size: 18px; font-weight: bold; color: #2563eb; }
          .confidential-notice { background: #fef2f2; border: 1px solid #fca5a5; padding: 15px; margin: 20px 0; text-align: center; }
        </style>
      </head>
      <body>
        ${document.getElementById('termsheet-content')?.innerHTML || 'No termsheet content available'}
      </body>
      </html>
    `;
    
    const blob = new Blob([htmlContent], { type: 'application/msword' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `Termsheet_${roundData.company?.name}_${roundData.round_name}.doc`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    
    addMessage?.('Termsheet exported to Word format', 'success');
  };

  const exportToGoogleDocs = () => {
    if (!roundData) return;
    
    // Get the termsheet content
    const termsheetElement = document.getElementById('termsheet-content');
    if (!termsheetElement) {
      addMessage?.('No termsheet content available to export', 'error');
      return;
    }
    
    // Create a Google Docs URL
    const googleDocsUrl = `https://docs.google.com/document/create?usp=docs_web&title=Termsheet_${roundData.company?.name}_${roundData.round_name}`;
    
    // Open in new tab
    window.open(googleDocsUrl, '_blank');
    addMessage?.('Opening Google Docs - you can copy and paste the termsheet content', 'info');
  };

  const exportToPDF = () => {
    if (!roundData) return;
    
    // Use the existing print functionality from TermsheetGenerator
    const printButton = document.querySelector('[data-print-termsheet]');
    if (printButton) {
      printButton.click();
    } else {
      addMessage?.('PDF export not available - please use the print function', 'error');
    }
  };

  useEffect(() => {
    loadRoundData();
  }, [loadRoundData]);

  // Close export menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showExportMenu && !event.target.closest('.export-menu-container')) {
        setShowExportMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showExportMenu]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!activeRound) {
    return (
      <div className="text-center py-12">
        <FileText className="mx-auto h-12 w-12 text-gray-400 mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">No Active Round</h3>
        <p className="text-gray-500">Create or select a fundraising round to generate a termsheet.</p>
      </div>
    );
  }

  if (!roundData) {
    return (
      <div className="text-center py-12">
        <AlertCircle className="mx-auto h-12 w-12 text-yellow-400 mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">Round Data Not Found</h3>
        <p className="text-gray-500">Unable to load round data for termsheet generation.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Termsheet Generator</h1>
          <p className="text-gray-600">
            Generate professional investment termsheets for <span className="font-medium">{selectedCompany?.name} - {activeRound.round_name}</span>
          </p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={loadRoundData}
            className="inline-flex items-center px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh Data
          </button>
          {termsheetGenerated && (
            <div className="relative export-menu-container">
              <button
                onClick={() => setShowExportMenu(!showExportMenu)}
                className="inline-flex items-center px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
              >
                <Download className="w-4 h-4 mr-2" />
                Export
              </button>
              {showExportMenu && (
                <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg z-10 border border-gray-200">
                  <div className="py-1">
                    <button
                      onClick={() => {
                        exportToWord();
                        setShowExportMenu(false);
                      }}
                      className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                    >
                      <WordIcon className="w-4 h-4 mr-3" />
                      Export to Word
                    </button>
                    <button
                      onClick={() => {
                        exportToGoogleDocs();
                        setShowExportMenu(false);
                      }}
                      className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                    >
                      <ExternalLink className="w-4 h-4 mr-3" />
                      Open in Google Docs
                    </button>
                    <button
                      onClick={() => {
                        exportToPDF();
                        setShowExportMenu(false);
                      }}
                      className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                    >
                      <FileText className="w-4 h-4 mr-3" />
                      Export to PDF
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Round Information Summary */}
      <div className="bg-white rounded-lg border p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Round Information</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
          <div>
            <p className="text-gray-600">Round Name</p>
            <p className="font-medium text-gray-900">{roundData.round_name}</p>
          </div>
          <div>
            <p className="text-gray-600">Round Type</p>
            <p className="font-medium text-gray-900 capitalize">{roundData.round_type}</p>
          </div>
          <div>
            <p className="text-gray-600">Target Amount</p>
            <p className="font-medium text-gray-900">
              {formatCurrencyWithMagnitude(roundData.target_amount || 0)}
            </p>
          </div>
          <div>
            <p className="text-gray-600">Pre-Money Valuation</p>
            <p className="font-medium text-gray-900">
              {formatCurrencyWithMagnitude(roundData.pre_money_valuation || 0)}
            </p>
          </div>
          <div>
            <p className="text-gray-600">Company</p>
            <p className="font-medium text-gray-900">{roundData.company?.name}</p>
          </div>
          <div>
            <p className="text-gray-600">Status</p>
            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
              termsheetGenerated 
                ? 'bg-green-100 text-green-800' 
                : 'bg-yellow-100 text-yellow-800'
            }`}>
              {termsheetGenerated ? 'Generated' : 'Pending'}
            </span>
          </div>
        </div>
        
        {/* Additional Round Details */}
        {(roundData.minimum_amount || roundData.maximum_amount || roundData.lead_investor_amount) && (
          <div className="mt-6 pt-6 border-t border-gray-200">
            <h4 className="text-md font-semibold text-gray-900 mb-3">Investment Details</h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
              {roundData.minimum_amount && (
                <div>
                  <p className="text-gray-600">Minimum Investment</p>
                  <p className="font-medium text-gray-900">
                    {formatCurrencyWithMagnitude(roundData.minimum_amount)}
                  </p>
                </div>
              )}
              {roundData.maximum_amount && (
                <div>
                  <p className="text-gray-600">Maximum Investment</p>
                  <p className="font-medium text-gray-900">
                    {formatCurrencyWithMagnitude(roundData.maximum_amount)}
                  </p>
                </div>
              )}
              {roundData.lead_investor_amount && (
                <div>
                  <p className="text-gray-600">Lead Investor Amount</p>
                  <p className="font-medium text-gray-900">
                    {formatCurrencyWithMagnitude(roundData.lead_investor_amount)}
                  </p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Termsheet Generator */}
      <div className="bg-white rounded-lg border">
        <div id="termsheet-content">
          <TermsheetGenerator
            company={roundData.company}
            roundData={{
              roundName: roundData.round_name,
              roundType: roundData.round_type,
              targetAmount: roundData.target_amount,
              preMoneyValuation: roundData.pre_money_valuation,
              minimumAmount: roundData.minimum_amount,
              maximumAmount: roundData.maximum_amount,
              leadInvestorAmount: roundData.lead_investor_amount,
              currency: roundData.currency,
              description: roundData.description,
              termsConfig: roundData.terms_config,
              useOfFunds: roundData.use_of_funds,
              excludedCountries: roundData.excluded_countries
            }}
            onSave={handleSaveTermsheet}
            saving={saving}
            showSaveButton={true}
          />
        </div>
      </div>

      {/* Instructions */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-blue-900 mb-3">How to Use</h3>
        <div className="space-y-2 text-sm text-blue-800">
          <p><strong>1. Review Round Data:</strong> Ensure all round information is complete and accurate.</p>
          <p><strong>2. Generate Termsheet:</strong> The termsheet will be automatically generated based on your round configuration.</p>
          <p><strong>3. Customize:</strong> Make any necessary adjustments to the terms and conditions.</p>
          <p><strong>4. Save & Share:</strong> Save the termsheet configuration and share with investors.</p>
        </div>
      </div>

      {/* Recent Activity */}
      {termsheetGenerated && (
        <div className="bg-white rounded-lg border p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Activity</h3>
          <div className="space-y-3">
            <div className="flex items-center text-sm">
              <div className="w-2 h-2 bg-green-500 rounded-full mr-3"></div>
              <span className="text-gray-600">Termsheet generated successfully</span>
              <span className="text-gray-400 ml-auto">
                {(() => {
                  const date = new Date();
                  const day = date.getDate();
                  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
                  const month = monthNames[date.getMonth()];
                  const year = date.getFullYear();
                  return `${day} ${month} ${year}`;
                })()}
              </span>
            </div>
            <div className="flex items-center text-sm">
              <div className="w-2 h-2 bg-blue-500 rounded-full mr-3"></div>
              <span className="text-gray-600">Configuration saved</span>
              <span className="text-gray-400 ml-auto">
                {(() => {
                  const date = new Date();
                  const day = date.getDate();
                  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
                  const month = monthNames[date.getMonth()];
                  const year = date.getFullYear();
                  return `${day} ${month} ${year}`;
                })()}
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TermsheetPage;
